// src/components/blog/search/SearchInput.tsx

'use client'

import { useState, useEffect, useRef } from 'react'
import RiSearchLine from '~/icons/remix/generated/RiSearchLine'
import RiCloseLine from '~/icons/remix/generated/RiCloseLine'
import { cn } from '~/lib/utils'
import { useSearch } from './SearchContext'

interface SearchInputProps {
  className?: string
  placeholder?: string
  isSticky?: boolean
}

export function SearchInput({ 
  className,
  placeholder = "Search articles...",
  isSticky = false,
}: SearchInputProps): JSX.Element {
  const { handleSearch, currentFilters: { q: initialQuery }, isLoading } = useSearch()
  const [query, setQuery] = useState(initialQuery)
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Sync input with URL params
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional resync of local state when the source prop changes
    setQuery(initialQuery)
  }, [initialQuery])

  // Global ⌘K / Ctrl+K to focus the search input (the on-input handler only
  // fires when the field is already focused, so the advertised shortcut was a
  // no-op from anywhere else on the page).
  //
  // Registered in the CAPTURE phase so this page-local claimant runs before
  // the site-wide palette listener (`SiteSearch.tsx`), which listens in the
  // bubble phase and backs off when `e.defaultPrevented` is set. Bubble-phase
  // registration on both sides made "first claimant wins" mean "first
  // *registered* wins" — correct on a hard load of /blog/search (this
  // component mounts before the dynamic-imported navbar chunk) but inverted
  // after SPA navigation (the navbar's listener predates this mount), so the
  // page's advertised ⌘K opened the global palette instead of focusing the
  // input. Capture precedes bubble on the same node regardless of
  // registration order, so on /blog/search this input deterministically wins
  // on both load paths.
  useEffect(() => {
    const onGlobalKeyDown = (e: KeyboardEvent): void => {
      if (!(e.metaKey || e.ctrlKey) || typeof e.key !== 'string' || e.key.toLowerCase() !== 'k') return
      // Don't hijack the shortcut while the user is typing in another editable
      // field (input/textarea/select/contenteditable) — only steal it from
      // non-editing contexts (or from our own search box).
      const active = document.activeElement
      const editingElsewhere =
        active instanceof HTMLElement &&
        active !== inputRef.current &&
        (active.isContentEditable ||
          active instanceof HTMLInputElement ||
          active instanceof HTMLTextAreaElement ||
          active instanceof HTMLSelectElement)
      if (editingElsewhere) return
      e.preventDefault()
      inputRef.current?.focus()
    }
    document.addEventListener('keydown', onGlobalKeyDown, { capture: true })
    return () => document.removeEventListener('keydown', onGlobalKeyDown, { capture: true })
  }, [])

  // Handle input changes - now just updates local state
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setQuery(e.target.value)
  }

  // Handle form submission
  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    void handleSearch(query)
  }

  // Clear search
  const handleClear = (): void => {
    setQuery('')
    void handleSearch('')
  }

  // Handle keyboard shortcuts (⌘K focus is handled by the global listener above)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    // Escape to clear and blur
    if (e.key === 'Escape') {
      handleClear()
      e.currentTarget.blur()
    }
  }

  return (
    <form 
      role="search"
      onSubmit={handleSubmit}
      className={cn(
        "relative",
        isSticky && "sticky top-0 z-20 -mx-4 bg-white px-4 py-4 shadow-sm dark:bg-gray-900",
        className
      )}
    >
      <div className="relative">
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isLoading}
          className={cn(
            "block w-full rounded-lg border-0 py-3 pl-4 pr-10",
            "text-gray-900 placeholder:text-gray-400",
            "ring-1 ring-inset ring-gray-300",
            "focus:ring-2 focus:ring-inset focus:ring-blue-600",
            "dark:bg-gray-800 dark:text-white dark:ring-gray-700",
            "dark:placeholder:text-gray-500 dark:focus:ring-blue-500",
            "transition-all duration-200",
            "sm:text-base sm:leading-6",
            isFocused && "ring-2 ring-blue-600 dark:ring-blue-500",
            isLoading && "cursor-wait opacity-70"
          )}
          aria-label="Search articles"
        />
        
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          {query && (
            <button
              type="button"
              onClick={handleClear}
              disabled={isLoading}
              className={cn(
                "p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300",
                "focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-500",
                "rounded-full",
                isLoading && "opacity-50 cursor-wait"
              )}
              aria-label="Clear search"
            >
              <RiCloseLine className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading}
            className={cn(
              "p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300",
              "focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-500",
              "rounded-full",
              isLoading && "opacity-50 cursor-wait"
            )}
            aria-label="Submit search"
          >
            <RiSearchLine className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Keyboard shortcut hint */}
      <div className="pointer-events-none absolute inset-y-0 right-0 hidden items-center pr-12 sm:flex">
        <kbd className="hidden rounded bg-gray-100 px-1.5 py-0.5 text-xs font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400 sm:inline-flex">
          ⌘K
        </kbd>
      </div>
    </form>
  )
} 